import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import { useSelector } from 'react-redux';
import {
  getAgentDashboardShares,
  addAgentDashboardShare,
  revokeAgentDashboardShare,
  clearAgentDashboardShares,
  agentDashboardSharesSelectors,
} from 'state/normalized/agentDashboard/agentDashboardSharesSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { useAppThunkDispatch } from 'state/store';
import { SLICE_STATUS, DATE, EMAIL_REGEX } from 'utils/constants';
import moment from 'utils/moment';

interface AgentDashboardSharesModalProps {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
}

const AgentDashboardSharesModal = ({
  isOpen,
  onClose,
  dashboardId,
}: AgentDashboardSharesModalProps) => {
  const dispatch = useAppThunkDispatch();

  const shares = useSelector(agentDashboardSharesSelectors.selectShares);
  const status = useSelector(agentDashboardSharesSelectors.selectStatus);
  const addStatus = useSelector(agentDashboardSharesSelectors.selectAddStatus);
  const revokeStatus = useSelector(
    agentDashboardSharesSelectors.selectRevokeStatus
  );
  const isAdding = addStatus === SLICE_STATUS.LOADING;
  const isRevoking = revokeStatus === SLICE_STATUS.LOADING;

  const [email, setEmail] = useState('');
  const [inputState, setInputState] = useState<'default' | 'error'>('default');
  const [errorMessage, setErrorMessage] = useState<string>();

  const fetchShares = useCallback(() => {
    dispatch(getAgentDashboardShares(dashboardId));
  }, [dispatch, dashboardId]);

  useEffect(() => {
    if (isOpen) {
      fetchShares();
    }
  }, [isOpen, fetchShares]);

  const resetInput = useCallback(() => {
    setEmail('');
    setInputState('default');
    setErrorMessage(undefined);
  }, []);

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      setInputState('default');
      setErrorMessage(undefined);
    },
    []
  );

  const handleAdd = useCallback(async () => {
    // Guard against double-submit (e.g. pressing Enter twice quickly), which
    // would fire a second POST that comes back 409 and shows a spurious error.
    if (isAdding) return;
    const trimmed = email.trim();
    // EMAIL_REGEX also matches the empty string, so guard for non-empty first.
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setInputState('error');
      setErrorMessage(intl.get('AGENT_DASHBOARD_SHARES_MODAL.INVALID_EMAIL'));
      return;
    }
    try {
      await dispatch(
        addAgentDashboardShare({ dashboardId, email: trimmed })
      ).unwrap();
      resetInput();
      dispatch(
        showNotification({
          notificationVariant: 'success',
          notificationTitle: intl.get(
            'AGENT_DASHBOARD_SHARES_MODAL.ADD_SUCCESS'
          ),
        })
      );
    } catch (status) {
      const isDuplicate = status === 409;
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            isDuplicate
              ? 'AGENT_DASHBOARD_SHARES_MODAL.DUPLICATE_ERROR'
              : 'AGENT_DASHBOARD_SHARES_MODAL.ADD_ERROR'
          ),
        })
      );
    }
  }, [dispatch, dashboardId, email, resetInput, isAdding]);

  const handleRevoke = useCallback(
    async (shareId: string) => {
      try {
        await dispatch(
          revokeAgentDashboardShare({ dashboardId, shareId })
        ).unwrap();
        dispatch(
          showNotification({
            notificationVariant: 'success',
            notificationTitle: intl.get(
              'AGENT_DASHBOARD_SHARES_MODAL.REVOKE_SUCCESS'
            ),
          })
        );
      } catch {
        dispatch(
          showNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get(
              'AGENT_DASHBOARD_SHARES_MODAL.REVOKE_ERROR'
            ),
          })
        );
      }
    },
    [dispatch, dashboardId]
  );

  const handleClose = useCallback(() => {
    resetInput();
    dispatch(clearAgentDashboardShares());
    onClose();
  }, [dispatch, onClose, resetInput]);

  return (
    <Modal
      aria-label={intl.get('AGENT_DASHBOARD_SHARES_MODAL.TITLE')}
      isOpen={isOpen}
      closeModal={handleClose}
      title={intl.get('AGENT_DASHBOARD_SHARES_MODAL.TITLE')}
      titleIcon={{
        name: 'share-social-outline',
        className: 'text-primary-dark',
      }}
      size='medium'
    >
      <div className='w-full flex flex-col gap-4'>
        <div className='flex items-end gap-2'>
          <TextField
            className='flex-1'
            value={email}
            placeholder={intl.get(
              'AGENT_DASHBOARD_SHARES_MODAL.EMAIL_PLACEHOLDER'
            )}
            state={inputState}
            helpText={errorMessage}
            onChange={handleEmailChange}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                handleAdd();
              }
            }}
            data-cy='agent-dashboard-share-email-input'
          />
          <Button
            variant='secondary'
            size='default'
            className='h-10'
            onClick={handleAdd}
            disabled={isAdding}
            data-cy='agent-dashboard-share-add-button'
          >
            {intl.get('AGENT_DASHBOARD_SHARES_MODAL.ADD_BUTTON')}
          </Button>
        </div>

        <Typography
          variant='body2'
          className='font-semibold text-tiny text-neutral-black'
        >
          {intl.get('AGENT_DASHBOARD_SHARES_MODAL.SHARED_WITH')}
        </Typography>

        {status === SLICE_STATUS.LOADING || status === SLICE_STATUS.IDLE ? (
          <div
            className='flex items-center gap-2 py-2 px-4 animate-pulse'
            data-cy='agent-dashboard-shares-loading'
          >
            <div className='w-8 h-8 rounded-full bg-neutral-lighter-two' />
            <div className='flex flex-col gap-1'>
              <div className='w-48 h-4 rounded bg-neutral-lighter-two' />
              <div className='w-24 h-3 rounded bg-neutral-lighter-two' />
            </div>
          </div>
        ) : status === SLICE_STATUS.FAILED ? (
          <div
            className='flex flex-col items-start gap-2 py-2 px-4'
            data-cy='agent-dashboard-shares-error'
          >
            <Typography variant='body2' className='text-neutral-dark'>
              {intl.get('AGENT_DASHBOARD_SHARES_MODAL.ERROR')}
            </Typography>
            <Button
              variant='tertiary'
              onClick={fetchShares}
              data-cy='agent-dashboard-shares-retry'
            >
              {intl.get('AGENT_DASHBOARD_SHARES_MODAL.RETRY')}
            </Button>
          </div>
        ) : shares.length === 0 ? (
          <Typography
            variant='body2'
            className='text-neutral-dark py-2 px-4'
            data-cy='agent-dashboard-shares-empty'
          >
            {intl.get('AGENT_DASHBOARD_SHARES_MODAL.EMPTY')}
          </Typography>
        ) : (
          <div className='flex flex-col gap-1'>
            {shares.map((share) => (
              <div
                key={share.id}
                className='flex items-center justify-between gap-2 py-2 px-4'
                data-cy={`agent-dashboard-share-${share.id}`}
              >
                <div className='flex items-center gap-2'>
                  <Avatar
                    imageSrc=''
                    initial={share.email.charAt(0).toUpperCase()}
                    name={share.email}
                  />
                  <div className='flex flex-col'>
                    <Typography variant='body2'>{share.email}</Typography>
                    <Typography variant='caption' className='text-neutral-dark'>
                      {intl.get('AGENT_DASHBOARD_SHARES_MODAL.SHARED_ON', {
                        date: moment(share.createdAt).format(DATE.SHORT_FORMAT),
                      })}
                    </Typography>
                  </div>
                </div>
                <IconButton
                  name='trash'
                  aria-label={intl.get('AGENT_DASHBOARD_SHARES_MODAL.REVOKE')}
                  onClick={() => handleRevoke(share.id)}
                  disabled={isRevoking}
                  className='text-base text-neutral-dark p-1'
                  data-cy={`agent-dashboard-share-revoke-${share.id}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AgentDashboardSharesModal;
